import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { FaUsersGear } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

const formatDate = d => (d ? new Date(d).toLocaleString() : '—');

export const AdminUsers = () => {

    const { api, user: currentUser } = useApp();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const loadUsers = useCallback(() => {

        setLoading(true);
        setError(null);

        return api.get('/api/admin/users')
            .then(d => setUsers(d?.users || []))
            .catch(e => setError(e?.err || e?.message || String(e)))
            .finally(() => setLoading(false));

    }, [api]);

    useEffect(() => {

        loadUsers();

    }, [loadUsers]);

    const updateUser = useCallback(async (id, body) => {

        setBusyId(id);
        setError(null);

        try {

            const data = await api.request('PATCH', `/api/admin/users/${id}`, body);
            setUsers(prev => prev.map(u => (u.id === id ? data.user : u)));

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setBusyId(null);

        }

    }, [api]);

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center gap-2 mb-1">
                <FaUsersGear className="text-primary" size={22} />
                <h3 className="m-0">
                    User Administration
                </h3>
            </div>
            <p className="text-muted">
                Promote users to admin or disable accounts. Disabled users are signed out on their next request.
                You can&apos;t change your own admin status — ask another admin if you need to step down.
            </p>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body>

                    {loading ? (
                        <Spinner animation="border" />
                    ) : users.length === 0 ? (
                        <Alert variant="light">
                            No users yet — they&apos;ll show up here on first sign-in.
                        </Alert>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle" size="sm">
                            <thead>
                                <tr>
                                    <th>
                                        User
                                    </th>
                                    <th>
                                        Last login
                                    </th>
                                    <th>
                                        Joined
                                    </th>
                                    <th className="text-center">
                                        Admin
                                    </th>
                                    <th className="text-center">
                                        Disabled
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <UserRow
                                        busy={busyId === u.id}
                                        isSelf={currentUser && currentUser.id === u.id}
                                        key={u.id}
                                        onUpdate={updateUser}
                                        user={u}
                                    />
                                ))}
                            </tbody>
                        </Table>
                    )}

                </Card.Body>
            </Card>

        </Container>

    );

};

const UserRow = ({ busy, isSelf, onUpdate, user }) => (
    <tr className={user.is_disabled ? 'text-muted' : ''}>
        <td>
            <div className="fw-semibold">
                {user.display_name || user.email}
                {isSelf && (
                <Badge bg="secondary" className="ms-2">
                    you
                </Badge>
                )}
            </div>
            <div className="text-muted small">
                {user.email}
            </div>
        </td>
        <td className="text-muted small">
            {formatDate(user.last_login_at)}
        </td>
        <td className="text-muted small">
            {formatDate(user.created_at)}
        </td>
        <td className="text-center">
            <Form.Check
                checked={!!user.is_admin}
                disabled={busy || isSelf}
                onChange={e => onUpdate(user.id, { is_admin: e.target.checked })}
                type="switch"
            />
        </td>
        <td className="text-center">
            {isSelf ? (
                <span className="text-muted small">
                    —
                </span>
            ) : (
                <Button
                    disabled={busy}
                    onClick={() => onUpdate(user.id, { is_disabled: !user.is_disabled })}
                    size="sm"
                    variant={user.is_disabled ? 'outline-success' : 'outline-danger'}
                >
                    {user.is_disabled ? 'Enable' : 'Disable'}
                </Button>
            )}
        </td>
    </tr>
);
